/**
 * useNotificationPrefs — read + subscribe to profiles/{uid}.notificationPrefs
 *
 * Returns:
 *   { prefs, ready }
 *
 *   • prefs — the merged settings object (defaults overlaid with the
 *     user's stored values). Safe to read every key unconditionally —
 *     mergeWithDefaults guarantees every route + quietHours exists.
 *   • ready — false until the first Firestore read returns. The
 *     settings page renders a "Loading preferences…" placeholder while
 *     this is false to avoid showing default toggles that then flip
 *     into the user's actual state a beat later.
 *
 * Realtime: onSnapshot subscription, so any change (this device or
 * another device the user is signed into) re-renders immediately.
 */

import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase.js'
import { mergeWithDefaults, buildDefaultNotificationPrefs } from '../utils/notificationPrefs.js'

export function useNotificationPrefs(uid) {
  const [prefs, setPrefs] = useState(() => buildDefaultNotificationPrefs())
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!uid) {
      setPrefs(buildDefaultNotificationPrefs())
      setReady(false)
      return
    }
    const unsub = onSnapshot(
      doc(db, 'profiles', uid),
      (snap) => {
        const data = snap.exists() ? snap.data() : {}
        setPrefs(mergeWithDefaults(data?.notificationPrefs))
        setReady(true)
      },
      (err) => {
        // Permission errors are non-fatal — we render defaults. Log
        // for visibility but don't surface to the UI.
        console.warn('[useNotificationPrefs] snapshot failed:', err?.code || err?.message)
        setReady(true)
      },
    )
    return () => unsub()
  }, [uid])

  return { prefs, ready }
}
